import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, Share, X } from 'lucide-react';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

// v2: bumped from `pwa-install-dismissed-at` (franklycatering#545) — the old
// key was written on ANY promptInstall() outcome, including cancelling the
// browser's own native dialog, so it could already be set (and silently
// suppressing the nudge for up to 14 days) on devices that hit that bug
// before this fix shipped. Bumping the key gives those users an immediate
// clean slate instead of waiting out a cooldown for a decision they didn't
// actually make.
const DISMISS_KEY = 'pwa-install-dismissed-at-v2';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function recentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
}

/**
 * Mobile install nudge for the staff/admin app.
 *
 * Scoped to `/admin/*` and to signed-in staff only (franklycatering#500) — the
 * app's `start_url` is `/`, which `RootRedirect` sends to `/login` for anyone
 * without a session, so nudging a public catering customer to install would
 * point them at a staff login screen. This mirrors franklycore's PWA, which is
 * itself an internal-only tool; the public ordering site is intentionally not
 * in scope here.
 *
 * - Android / Chrome: shows an "Install" button wired to the native prompt
 *   captured by usePwaInstall (only rendered once that prompt is available).
 * - iOS Safari: no programmatic prompt exists, so it shows the manual
 *   "Share → Add to Home Screen" steps instead.
 *
 * Mounted at `RootLayout` (alongside `FeedbackWidget`, same precedent) rather
 * than inside the admin route tree, so the `beforeinstallprompt` listener is
 * live from first paint — Chrome fires that event once per page load, and a
 * listener mounted only after the staff login→admin navigation would miss it.
 *
 * Hidden on desktop, when already installed (standalone), and for 14 days
 * after the user dismisses IT — either via the "X" button on this banner, or
 * by completing the native install (`accepted`). Cancelling the browser's own
 * native install dialog (`dismissed`) is a different signal and does NOT
 * start that cooldown (franklycatering#545): the browser controls when that
 * dialog reappears, not us, so our banner stays available to give the user
 * another way back in on a later visit.
 */
export function InstallPrompt() {
  const { user } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { isStandalone, isIos, canPrompt, promptInstall } = usePwaInstall();
  // Start dismissed so nothing flashes before the localStorage check runs.
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(recentlyDismissed());
  }, []);

  const isAdminRoute = location.pathname.startsWith('/admin');

  if (!user || !isAdminRoute) return null;
  if (dismissed || isStandalone || !isMobile) return null;
  // Android needs the captured native prompt; iOS falls back to instructions.
  if (!canPrompt && !isIos) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Private mode / storage disabled — just hide for this session.
    }
    setDismissed(true);
  };

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      // Installed. `appinstalled` will also flip `isStandalone` and hide this
      // component independently, but persist the cooldown too as a
      // belt-and-suspenders (e.g. covers a slow/missed `appinstalled` event).
      dismiss();
    } else if (outcome === 'dismissed') {
      // The user cancelled the browser's OWN native install dialog — not this
      // banner. That's not a "stop nagging me" signal about our nudge, so
      // don't persist the cooldown: leave the door open for this banner to
      // reappear on a later visit/session (franklycatering#545). Just hide
      // for the remainder of the current page load, without touching
      // localStorage, as a defensive no-immediate-renag guard.
      setDismissed(true);
    }
    // 'unavailable' can't happen here — the Install button only renders once
    // canPrompt is true — but leave it as a no-op rather than assume.
  };

  return (
    // Positioned above the bottom-right FeedbackWidget FAB (fixed bottom-6
    // right-6, z-[60]) — same signed-in condition, so on mobile admin routes
    // both can render at once. `bottom-[5.5rem]` clears the FAB's ~72px
    // footprint instead of overlapping it (which would make this card's
    // dismiss button unreachable, since the FAB sits at a higher z-index).
    <div className="fixed inset-x-0 z-50 p-3 bottom-[calc(5.5rem+env(safe-area-inset-bottom))]">
      <div className="mx-auto flex max-w-md items-center gap-3 rounded-xl border border-border bg-card p-3 shadow-lg">
        <img
          src="/icon-192.png"
          alt=""
          className="h-11 w-11 shrink-0 rounded-lg"
          width={44}
          height={44}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Install Tristano's</p>
          {isIos ? (
            <p className="text-xs text-muted-foreground">
              Tap <Share className="inline h-3.5 w-3.5 align-text-bottom" aria-label="Share" /> then
              &ldquo;Add to Home Screen&rdquo;
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Add it to your home screen for one-tap access.
            </p>
          )}
        </div>
        {!isIos && (
          <Button
            size="sm"
            onClick={handleInstall}
            className="min-h-[44px] shrink-0"
          >
            <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Install
          </Button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
